import dynamic from 'next/dynamic';

import React, { useState, Fragment, useEffect } from "react";
import Image from "next/image";
import { toast } from "react-toastify";
import Modal from "@/components/ui/Modal";
import MessageModal from "@/components/ui/Message-Modal";
import { getUserHandles, updateUserHandle } from "@/utils/firebaseFunctions";
import { checkFailedBindsForDisabling, watchFailedBindTries } from "@/utils/helpers/helperFunctions";

const Button = dynamic(() => import('@/components/ui/Button'));

function EmailModal({ isOpen, onClose, uid, givenUser, ipAddress }) {
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationStage, setVerificationStage] = useState("emailPassword");
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [claiming, setClaiming] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [verified, setVerified] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [activeError, setActiveError] = useState(null);
  const [activeErrorTitle, setActiveErrorTitle] = useState(null);

  useEffect(() => {
    let interval;
    if (resendDisabled) {
      interval = setInterval(() => {
        setCountdown((prevCount) => prevCount - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [resendDisabled]);

  useEffect(() => {
    if (countdown === 0) {
      setResendDisabled(false);
      setCountdown(60); // Reset countdown
    }
  }, [countdown]);

  useEffect(() => {
    if (emailSent) {
      setResendDisabled(true); // Start countdown when verificationStage changes to "code"
    }
  }, [emailSent]);

  const sendEmailVerification = async () => {
    try {
      if(!checkFailedBindsForDisabling({ id: "email" })){
        setActiveError("Too many failed attempts. Please try again later.");
        setActiveErrorTitle("Error");
        setShowErrorModal(true);
        return;
      }

      if (!email) {
        setActiveError("Email required.");
        setActiveErrorTitle("Error");
        setShowErrorModal(true);
        toast.error("Email required.");
        return;
      }
      if (!uid) {
        setActiveError("No UID attached.");
        setActiveErrorTitle("Error");
        setShowErrorModal(true);
        toast.error("Login first. No uid attached.");
        return;
      }
      // Regex pattern to validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setActiveError("Invalid email address.");
        setActiveErrorTitle("Error");
        setShowErrorModal(true);
        toast.error("Invalid email address.");
        return;
      }

      setSending(true);

      const url = `https://us-central1-prodmxs.cloudfunctions.net/sendEmailVerification`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "aad235a0-7fcc-079f-e33f-53c65fbc4c81",
        },
        body: JSON.stringify({
          uid: uid,
          email: email
        }),
      });


      if (!response.ok) {
        const data = await response.json();
        setActiveError(`${data.msg}`);
        setActiveErrorTitle("Error");
        setShowErrorModal(true);
        watchFailedBindTries({ id: "email" }, givenUser);
        toast.error(`${data.msg}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setVerificationStage("code");
      setEmailSent(true);
    } catch (error) {
      console.error("Error sending email: ", error);
      watchFailedBindTries({ id: "email" }, givenUser);
    } finally {
      setSending(false);
    }
  };

  const checkVerificationCode = async () => {
    try {
      if(!checkFailedBindsForDisabling({ id: "email" })){
        setActiveError("Too many failed attempts. Please try again later.");
        setActiveErrorTitle("Error");
        setShowErrorModal(true);
        return;
      }

      if (!uid) {
        setActiveError("Login First. No UID attached.");
        setActiveErrorTitle("Error");
        setShowErrorModal(true);
        toast.error(`No UID attached.`);
        return;
      }

      if (!verificationCode) {
        setActiveError("Verification Code required.");
        toast.error(`Verification Code required.`);
        setActiveErrorTitle("Error");
        setShowErrorModal(true);
        return;
      }

      setChecking(true);

      const url = `https://us-central1-prodmxs.cloudfunctions.net/verifyEmail`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "aad235a0-7fcc-079f-e33f-53c65fbc4c81",
        },
        body: JSON.stringify({
          uid: uid,
          code: verificationCode,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        watchFailedBindTries({ id: "email" }, givenUser);
        throw new Error(`${data.msg}`);
      }

      const data = await response.json();

      const handles = await getUserHandles(uid);
      let handle = handles?.filter((h) => h?.handle_name === "email")[0];
      if (handle) {
        handle.handle_verified = true;
        givenUser.email = email;
      }
      await updateUserHandle(uid, "email", handle);
      setVerified(true);
      if (!handle.handle_reward_claimed) {
        await handleRewardEmail();
      } else {
        window.location.reload();
      }
    } catch (error) {
      setActiveError(`Error checking verification code: ${error.message}`);
      setActiveErrorTitle("Error");
      setShowErrorModal(true);
      watchFailedBindTries({ id: "email" }, givenUser);
    } finally {
      setChecking(false);
    }
  };

  async function handleRewardEmail() {
    const url = `https://us-central1-prodmxs.cloudfunctions.net/addReward`;

    try {
      setClaiming(true);
      let data;

      data = {
        uid: uid,
        sub_event: "EMAIL",
        event: "REWARD",
        type: "UPDATE",
        comment: "E-mail verification.",
        game_name: "ecosystem",
        ip_address: ipAddress,
        mxs_gold_claimed: 10,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "aad235a0-7fcc-079f-e33f-53c65fbc4c81",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const data = await response.json();
        setShowSuccessModal(true);
        const handles = await getUserHandles(uid);
        let handle = handles?.filter((h) => h?.handle_name === "email")[0];
        if (handle) {
          handle.handle_reward_claimed = true;
        }
        await updateUserHandle(uid, "email", handle);
        setVerified(true);
        setClaiming(false);

      } else {
        const error = await response.json();
        console.error(error);
        setClaiming(false);
        setActiveError(error?.msg);
        setActiveErrorTitle("Error");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error(error);
      setClaiming(false);
      setActiveError(error?.message);
      setActiveErrorTitle("Error");
      setShowErrorModal(true);
    }
  }

  return (
    <div data-testid="email-modal">
      <Modal
        activeModal={isOpen}
        onClose={onClose}
        centered={true}
        title={"Verify your Email"}
        className="max-w-xl bg-metaxseed-dark-gray"
      >
        <div className="bg-metaxseed-dark-gray">
          <div>
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center space-x-4 max-w-52">
                <div className="logo-icon">
                  <Image
                    src="https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/branding%2Fmxs_games%2Fv1%2Fwhite%2Fpng%2Fmxs_icon_white.png?alt=media&token=8a7e36ba-954b-46b3-bcb2-559cec885bdf"
                    height={411}
                    width={370}
                    alt=""
                  />
                </div>

                <div>
                  <Image
                    src="https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/branding%2Fmxs_games%2Fv1%2Fwhite%2Fpng%2Fmxs_text_text.png?alt=media&token=5d1cd22e-0907-4594-a3cd-d5e8d520037f"
                    alt=""
                    className="h-8 mt-1"
                    width={1533}
                    height={330}
                  />
                </div>
              </div>
            </div>
            {verificationStage === "emailPassword" ? (
              <Fragment>
                <p className="text-center text-sm gothamBook text-metaxseed-text-gray mb-8">
                  Thanks for being a loyal MXS Games Member.
                  <br />
                  Please verify your email address to proceed.
                </p>
                <div className="my-2 pt-2 pb-4 w-full">
                  <input
                    type="email"
                    data-testid="email-input"
                    placeholder="Enter your Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 border-zinc-600 placeholder-zinc-400 text-white"
                  />
                </div>
                <div className="pt-4">
                  {sending ? (
                    <Button
                      dataTestId={"sending-email-verification-button"}
                      text="Sending"
                      className="font-normal btn-secondary gap-4 w-full !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                      isLoading
                    />
                  ) : (
                    <Button
                      icon="oui:email"
                      text="Send Email Verification"
                      iconClass="text-lg"
                      dataTestId={"send-email-verification-button"}
                      className="hover:jello-horizontal font-normal w-full btn-primary !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                      onClick={sendEmailVerification}
                      disabled={sending}
                    />
                  )}
                </div>
              </Fragment>
            ) : (
              <Fragment>
                <div>
                  <h6 className="text-white text-center mb-4 gothamBook">
                    Enter your verification code.
                  </h6>
                </div>
                <p className="text-center mb-8 text-sm gothamBook text-metaxseed-text-gray ">
                  We have sent a verification code to your email
                  <br />
                  <span className="text-metaxseed-light-blue">{email}</span>
                </p>
                <div className="my-2 pb-4 w-full">
                  <input
                    type="text"
                    data-testid="verification-code-input"
                    placeholder="Enter Verification Code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    className="font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 border-zinc-600 placeholder-zinc-400 text-white"
                  />
                </div>
                <div className="pt-4">
                  {checking ? (
                    <Button
                      dataTestId={"verification-code-checking-button"}
                      text="Sending"
                      className="font-normal btn-secondary gap-4 w-full !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                      isLoading
                    />
                  ) : (
                    <Button
                      dataTestId={"verification-code-check-button"}
                      text="Verify Email"
                      iconClass="text-lg"
                      className="hover:jello-horizontal font-normal w-full btn-primary !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                      onClick={checkVerificationCode}
                      disabled={checking || verified}
                    />
                  )}
                  {checking || countdown === 60 ? null : (
                    <div className="mt-4">
                      <Button
                        dataTestId={"resend-email-verification-button"}
                        text={
                          resendDisabled
                            ? `Resend Code (${countdown})`
                            : "Resend Code"
                        }
                        iconClass="text-lg"
                        className="font-normal w-full btn-primary !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                        onClick={() => {
                          if (countdown >= 60) sendEmailVerification();
                        }}
                        disabled={countdown < 60}
                      />
                    </div>
                  )}
                </div>
              </Fragment>
            )}
          </div>
        </div>
      </Modal>

      {showSuccessModal && (
        <MessageModal
          title={`Success!`}
          fireworks={true}
          status={true}
          centered
          activeModal={showSuccessModal}
          onClose={() => {
            window.location.reload();
          }}
        >
          <div className="flex items-center" data-testid="success-message-modal-content">
            <Image
              src={
                "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fpage_nodes%2Fnode_chest.png?alt=media&token=f064df08-466f-438a-b1df-e0ffdf519a30&w=384&q=75"
              }
              width={285}
              height={285}
              alt=""
              className="mb-4 max-h-[100px] xl:max-h-[200px] !max-w-fit mx-auto"
            />
          </div>
          <div className="font-medium py-4 text-center">
            <span className="font-bold">10 MXS GOLD</span> claimed successfully!
          </div>
        </MessageModal>
      )}

      {showErrorModal && (
        <MessageModal
          title={activeErrorTitle || "Error"}
          status={false}
          centered
          activeModal={showErrorModal}
          className="max-w-xl errorModalShadow"
          onClose={(event) => {
            if(event){
            setShowErrorModal(false);
            setTimeout(() => {
              setActiveError(null);
              setActiveErrorTitle(null);
              setClaiming(false);
            }, 300);
            }
          }}
        >
          <div className="font-medium py-2" data-testid="error-message-modal-content">
            {activeError || "An error occurred. Please try again later."}
          </div>
        </MessageModal>
      )}
    </div>
  );
}

export default EmailModal;
